import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import TextField from './fields/text-field';
import CardGroupField from './fields/card-group-field';
import { OTHER_VALUE } from '../constants/fields-values';
import fieldTypes from '../constants/field-types';

const { Box, ControlGroup, InputGroupField, CheckboxGroupField, RadioButtonGroupField } =
  designSystem;
const { useWatch } = reactHookForm;

const getComponent = (type) => {
  switch (type) {
    case fieldTypes.TEXTAREA:
      return TextField;
    case fieldTypes.CARD:
      return CardGroupField;
    case fieldTypes.CHECKBOX:
      return CheckboxGroupField;
    case fieldTypes.RADIO:
      return RadioButtonGroupField;
    case fieldTypes.INPUT:
      return InputGroupField;
    default:
      return () => null;
  }
};

const DifmOrderFormFieldsSection = ({
  activeStepFieldIds,
  activeStepFieldNames,
  control,
  getValues,
  fields,
}) => {
  const activeFields = fields.filter(
    (field) => activeStepFieldIds.includes(field.id) && !field.isSubField
  );

  // Watch for changes in the form to trigger re-render & show\hide subfields
  useWatch({ control, name: activeStepFieldNames });
  const renderFields = useCallback(() => {
    const extractFieldToList = (fieldProps = {}, list = []) => {
      const values = getValues();

      const fieldValue = values[fieldProps.name];
      const value = Array.isArray(fieldValue)
        ? fieldValue.find((val) => val === OTHER_VALUE)
        : fieldValue;
      const subFieldId = value && fieldProps.subFields && fieldProps.subFields[value];
      const subField = subFieldId && fields.find((field) => field.id === subFieldId);

      const newList = [...list, fieldProps];

      if (subField) {
        // Recursively call function to get sub field
        return extractFieldToList(subField, newList);
      }

      return newList;
    };

    const fieldList = activeFields.reduce(
      (acc, field) => [...acc, ...extractFieldToList(field)],
      []
    );

    return (
      <div>
        {fieldList.map(({ type, title, description, required, ...fieldProps }, index) => {
          const Component = getComponent(type);
          const boxMarginTop = fieldProps.isSubField ? 'spacing-3-xs' : 'spacing-xs';
          const isFirstItem = index === 0;

          return (
            <Box {...(!isFirstItem && { marginTop: boxMarginTop })}>
              {title ? (
                <ControlGroup
                  key={fieldProps.id}
                  required={required}
                  title={title}
                  description={description}
                >
                  <Component control={control} {...fieldProps} />
                </ControlGroup>
              ) : (
                <Component key={fieldProps.id} control={control} {...fieldProps} />
              )}
            </Box>
          );
        })}
      </div>
    );
  }, [fields, control, getValues, activeFields]);

  return renderFields();
};

DifmOrderFormFieldsSection.propTypes = {
  activeStepFieldIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStepFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  getValues: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default DifmOrderFormFieldsSection;
